import { useCallback, useEffect, useMemo, useState } from 'react';
import { capitalizeFirstWord, formatCurrency, multiplyMoney } from '@peles/shared';
import type {
  PlanningItem,
  VendorCategory,
  CatalogItem,
  ExpenseItemWithRelations,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { ItemAutosuggest } from './ItemAutosuggest';
import { CatalogItemQuickEditModal } from './CatalogItemQuickEditModal';
import { useDuplicateNameGuard, isSameName } from '../../hooks/useDuplicateNameGuard';
import { RoomAutosuggest } from './RoomAutosuggest';
import { ItemTasksSection } from './ItemTasksSection';
import { SettingListSelect } from '../floor-plan/SettingListSelect';
import { useCreateSettingsCatalogItem } from '../../hooks/useSettings';
import { useCreateRoom, useRooms } from '../../hooks/useFloorPlans';
import { useExpenseItems } from '../../hooks/useExpenses';
import { useItemUnits } from '../../hooks/useItemUnits';
import { useAddEventItems, useUpdatePlanningItem } from '../../hooks/usePlanning';
import { extractApiErrorMessage } from '../../utils/apiError';

interface PlanningItemModalProps {
  open: boolean;
  onClose: () => void;
  /** Existing item when editing; null when creating. */
  item: PlanningItem | null;
  /** Event that scopes the expense-line selector. */
  eventId: string;
  /** Event venue — its rooms populate the Room selector. */
  venueId?: string | null;
  /**
   * Pre-selected expense line for new rows (e.g. when the itemized list was
   * opened by zooming into an expense). Also seeds the category.
   */
  defaultExpenseItemId?: string | null;
  categories: VendorCategory[];
  catalogItems: CatalogItem[];
}

export function expenseLineLabel(expense: ExpenseItemWithRelations): string {
  const name = expense.itemName ?? expense.vendorCategoryName ?? 'Expense';
  const vendor = expense.vendorName ? ` — ${expense.vendorName}` : '';
  const price = formatCurrency(expense.price);
  return `${name}${vendor} (${price})`;
}

export function PlanningItemModal({
  open,
  onClose,
  item,
  eventId,
  venueId,
  defaultExpenseItemId,
  categories,
  catalogItems,
}: PlanningItemModalProps) {
  const isEdit = item !== null;
  const { data: rooms = [] } = useRooms(venueId ?? undefined, !!venueId);
  const { data: expenseItems = [] } = useExpenseItems({ eventId });
  const units = useItemUnits();

  const addItems = useAddEventItems();
  const updateItem = useUpdatePlanningItem();
  const createCatalogItem = useCreateSettingsCatalogItem();
  const createRoom = useCreateRoom();

  const [vendorCategoryId, setVendorCategoryId] = useState(item?.vendorCategoryId ?? '');
  const [catalogItemId, setCatalogItemId] = useState(item?.catalogItemId ?? '');
  // Item auto-detects list-vs-free-text: the input shows the name and the id
  // is set only when an existing entry is chosen. New rows start empty — the
  // item must be typed or picked every time.
  const [itemName, setItemName] = useState(
    item ? (item.itemName ?? item.catalogItemName ?? '') : '',
  );
  const [quantity, setQuantity] = useState(String(item?.quantity ?? 1));
  const [roomId, setRoomId] = useState(item?.roomId ?? '');
  const [roomName, setRoomName] = useState(item?.roomName ?? '');
  // Once the user touches the room field we stop auto-defaulting it.
  const [roomTouched, setRoomTouched] = useState(false);
  const [expenseItemId, setExpenseItemId] = useState(
    item?.expenseItemId ?? (item ? '' : (defaultExpenseItemId ?? '')),
  );
  const [unitPrice, setUnitPrice] = useState(String(item?.unitPrice ?? 0));
  // A price typed by hand is never overwritten by a catalog item's default.
  const [priceTouched, setPriceTouched] = useState(isEdit);
  const [unit, setUnit] = useState<string | null>(item?.unit ?? null);
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [error, setError] = useState('');
  const [quickCatalogItemId, setQuickCatalogItemId] = useState<string | null>(null);
  const confirmDuplicate = useDuplicateNameGuard();

  // Only active categories are selectable, sorted alphabetically by name.
  const sortedCategories = useMemo(
    () => categories.filter((c) => c.isActive).sort((a, b) => a.name.localeCompare(b.name)),
    [categories],
  );
  const filteredCatalogItems = useMemo(
    () =>
      catalogItems
        .filter((c) => c.vendorCategoryId === vendorCategoryId)
        .sort((a, b) => a.name.localeCompare(b.name)),
    [catalogItems, vendorCategoryId],
  );
  // Rooms come from the event venue only.
  const venueRooms = useMemo(
    () => rooms.filter((r) => r.venueId === venueId).sort((a, b) => a.name.localeCompare(b.name)),
    [rooms, venueId],
  );

  // New rows seed the category from the (default or chosen) expense line.
  useEffect(() => {
    if (isEdit || vendorCategoryId || !expenseItemId) return;
    const expense = expenseItems.find((e) => e.id === expenseItemId);
    if (expense?.vendorCategoryId) setVendorCategoryId(expense.vendorCategoryId);
  }, [isEdit, vendorCategoryId, expenseItemId, expenseItems]);

  // A venue with a single room defaults new rows to it.
  useEffect(() => {
    if (isEdit || roomTouched || roomId || roomName) return;
    if (venueRooms.length === 1) {
      setRoomId(venueRooms[0].id);
      setRoomName(venueRooms[0].name);
    }
  }, [isEdit, roomTouched, roomId, roomName, venueRooms]);

  const qtyNum = parseFloat(quantity) || 0;
  const priceNum = parseFloat(unitPrice) || 0;
  const total = multiplyMoney(qtyNum, priceNum);

  /**
   * Picking a catalog item starts the line at its default price and unit —
   * the price only while nobody has typed one, the unit only while none is
   * chosen.
   */
  const applyCatalogDefaults = useCallback(
    (catalogId: string | null) => {
      const entry = catalogId ? catalogItems.find((c) => c.id === catalogId) : undefined;
      if (!entry) return;
      if (!priceTouched && entry.defaultUnitPrice != null) {
        setUnitPrice(String(entry.defaultUnitPrice));
      }
      if (entry.unit) setUnit((current) => current ?? entry.unit);
    },
    [catalogItems, priceTouched],
  );

  // Every planning line itemizes an expense line — the link is mandatory.
  const isValid =
    vendorCategoryId !== '' && (!!catalogItemId || !!itemName.trim()) && expenseItemId !== '';

  // Free-text room names can be added to the venue on the fly (default 40x40 ft
  // footprint — editable later in the venue's floor-plan settings).
  const handleCreateRoom = useCallback(
    (name: string) => {
      if (!venueId) return;
      createRoom.mutate(
        { venueId, name, widthFt: 40, heightFt: 40 },
        {
          onSuccess: (room) => {
            setRoomName(room.name);
            setRoomId(room.id);
          },
          onError: () => setError('Failed to add room to the venue'),
        },
      );
    },
    [createRoom, venueId],
  );

  const handleCreateItem = useCallback(
    async (name: string) => {
      if (!vendorCategoryId) return;
      // Same-category exact matches are suppressed by the autosuggest, so
      // duplicates found here live in other categories — name them in the warning.
      const duplicateCategoryNames = [
        ...new Set(
          catalogItems
            .filter((c) => isSameName(c.name, name))
            .map((c) => categories.find((cat) => cat.id === c.vendorCategoryId)?.name ?? 'Unknown'),
        ),
      ];
      if (
        duplicateCategoryNames.length > 0 &&
        !(await confirmDuplicate({
          entityLabel: 'catalog item',
          name,
          detail: `(category: ${duplicateCategoryNames.join(', ')})`,
        }))
      ) {
        return;
      }
      createCatalogItem.mutate(
        { vendorCategoryId, name },
        {
          onSuccess: (catalogItem) => {
            setItemName(catalogItem.name);
            setCatalogItemId(catalogItem.id);
            setQuickCatalogItemId(catalogItem.id);
          },
          onError: () => setError('Failed to create catalog item'),
        },
      );
    },
    [createCatalogItem, vendorCategoryId, catalogItems, categories, confirmDuplicate],
  );

  const isPending = addItems.isPending || updateItem.isPending;

  const handleSubmit = useCallback(async () => {
    setError('');
    // A chosen catalog item wins; otherwise the typed text is free text.
    const catalogVal = catalogItemId || null;
    const itemNameVal = catalogItemId
      ? undefined
      : capitalizeFirstWord(itemName.trim()) || undefined;

    try {
      // Rooms are stored by id only — a typed room name that was never picked
      // from the dropdown resolves to an existing venue room (case-insensitive)
      // or is created on the venue, so free text is never silently dropped.
      let roomIdVal = roomId || null;
      const typedRoom = roomName.trim();
      if (!roomIdVal && typedRoom && venueId) {
        const existing = venueRooms.find(
          (r) => r.name.trim().toLowerCase() === typedRoom.toLowerCase(),
        );
        if (existing) {
          roomIdVal = existing.id;
        } else {
          const created = await createRoom.mutateAsync({
            venueId,
            name: typedRoom,
            widthFt: 40,
            heightFt: 40,
          });
          roomIdVal = created.id;
        }
      }

      if (isEdit && item) {
        await updateItem.mutateAsync({
          itemId: item.id,
          updates: {
            vendorCategoryId: vendorCategoryId || null,
            catalogItemId: catalogVal,
            itemName: itemNameVal ?? null,
            quantity: qtyNum,
            roomId: roomIdVal,
            expenseItemId,
            unitPrice: priceNum,
            unit,
            notes: notes.trim() || null,
          },
        });
      } else {
        await addItems.mutateAsync({
          eventId,
          items: [
            {
              vendorCategoryId: vendorCategoryId || null,
              catalogItemId: catalogVal,
              itemName: itemNameVal,
              quantity: qtyNum,
              roomId: roomIdVal,
              expenseItemId,
              unitPrice: priceNum,
              unit,
              notes: notes.trim() || undefined,
            },
          ],
        });
      }
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'The item could not be saved'));
    }
  }, [
    vendorCategoryId,
    catalogItemId,
    itemName,
    qtyNum,
    roomId,
    roomName,
    venueId,
    venueRooms,
    expenseItemId,
    priceNum,
    unit,
    notes,
    isEdit,
    item,
    eventId,
    createRoom,
    updateItem,
    addItems,
    onClose,
  ]);

  return (
    <EntityModal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit item' : 'Add an item'}
      size="lg"
      footer={
        <div className="flex gap-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
            className="px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
            data-testid="planning-item-submit"
          >
            {isPending ? 'Saving…' : isEdit ? 'Save' : 'Add item'}
          </button>
        </div>
      }
    >
      {error && (
        <div className="mb-4 px-3 py-2 bg-red-50 text-red-700 text-sm rounded">{error}</div>
      )}

      <div className="space-y-4">
        {/* Category */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
          <SearchableListSelect
            items={sortedCategories}
            value={vendorCategoryId}
            onChange={(v) => {
              setVendorCategoryId(v as string);
              setCatalogItemId('');
            }}
            getValue={(c) => c.id}
            getLabel={(c) => c.name}
            placeholder="Select category..."
            ariaLabel="Category"
          />
        </div>

        {/* Item — search the catalog or type a new one (auto-detected) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Item *</label>
          <ItemAutosuggest
            value={itemName}
            items={filteredCatalogItems}
            disabled={!vendorCategoryId}
            onChange={(name, id) => {
              setItemName(name);
              setCatalogItemId(id ?? '');
              applyCatalogDefaults(id ?? null);
            }}
            onCreateItem={handleCreateItem}
            selectedId={catalogItemId || null}
            onEditItem={(id) => setQuickCatalogItemId(id)}
          />
        </div>

        {/* Expense line — mandatory; every planning line itemizes an expense */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Need *</label>
          <SearchableListSelect
            items={expenseItems}
            value={expenseItemId}
            onChange={(v) => setExpenseItemId(v as string)}
            getValue={(e) => e.id}
            getLabel={(e) => expenseLineLabel(e)}
            placeholder="Which need is this for?"
            ariaLabel="Need"
          />
          {expenseItems.length === 0 && (
            <p className="mt-1 text-xs text-amber-600">
              Nothing planned yet for this event — add a need on the Plan first.
            </p>
          )}
        </div>

        {/* Room — pick from the venue's rooms or type a new one to add it */}
        {venueId && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Room</label>
            <RoomAutosuggest
              value={roomName}
              rooms={venueRooms}
              onChange={(name, id) => {
                setRoomTouched(true);
                setRoomName(name);
                setRoomId(id ?? '');
              }}
              onCreateRoom={handleCreateRoom}
            />
          </div>
        )}

        {/* Quantity + Unit Price + Total */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
            <input
              type="number"
              value={quantity}
              min="0"
              step="1"
              onChange={(e) => setQuantity(e.target.value)}
              aria-label="Quantity"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Unit Price</label>
            <input
              type="number"
              value={unitPrice}
              min="0"
              step="0.01"
              onChange={(e) => {
                setPriceTouched(true);
                setUnitPrice(e.target.value);
              }}
              aria-label="Unit price"
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Total</label>
            <div
              data-testid="planning-item-total"
              className="w-full px-3 py-2 border border-gray-200 bg-gray-50 rounded-md text-sm text-gray-700"
            >
              {formatCurrency(total)}
            </div>
          </div>
        </div>

        <SettingListSelect
          options={units}
          value={unit}
          onChange={setUnit}
          label="Unit"
          emptyMessage="No units yet — add them in Settings → App Settings"
          testId="item-unit"
        />

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={2}
            aria-label="Notes"
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none"
          />
        </div>

        {isEdit && item ? (
          <ItemTasksSection itemId={item.id} />
        ) : (
          <p className="text-xs text-gray-500">
            Once the item is added, tasks about it can be added here.
          </p>
        )}
      </div>

      <CatalogItemQuickEditModal
        catalogItemId={quickCatalogItemId}
        open={!!quickCatalogItemId}
        onClose={() => setQuickCatalogItemId(null)}
      />
    </EntityModal>
  );
}
